import React from 'react';
import { useCountdown } from '../hooks/useCountdown';

interface CountdownTimerProps {
  targetDate: Date;
  size?: 'small' | 'medium' | 'large';
  variant?: 'default' | 'compact';
  onExpired?: () => void;
}

const CountdownTimer: React.FC<CountdownTimerProps> = ({ 
  targetDate, 
  size = 'small',
  variant = 'default',
  onExpired 
}) => {
  const { days, hours, minutes, seconds, isExpired } = useCountdown(targetDate);

  React.useEffect(() => {
    if (isExpired && onExpired) {
      onExpired();
    }
  }, [isExpired, onExpired]);

  if (isExpired) {
    return (
      <div className="text-red-600 font-semibold text-sm">
        ¡Oferta expirada!
      </div>
    );
  }

  const sizeClasses = {
    small: 'text-xs',
    medium: 'text-sm',
    large: 'text-base'
  };

  if (variant === 'compact') {
    return (
      <div className={`flex items-center space-x-1 ${sizeClasses[size]}`}>
        <span className="text-red-600 font-semibold">⏰</span>
        <span className="text-red-700 font-medium">
          {days > 0 && `${days}d `}
          {String(hours).padStart(2, '0')}:
          {String(minutes).padStart(2, '0')}:
          {String(seconds).padStart(2, '0')}
        </span>
      </div>
    );
  }

  return (
    <div className="bg-gradient-to-r from-red-50 to-orange-50 border border-red-200 rounded-lg p-3">
      <div className="flex items-center justify-between mb-2">
        <span className={`text-red-800 font-semibold ${sizeClasses[size]}`}>
          ⏰ ¡Oferta termina en:
        </span>
      </div>
      
      <div className="flex space-x-2">
        {days > 0 && (
          <div className="bg-red-600 text-white rounded px-2 py-1 text-center min-w-[40px]">
            <div className={`font-bold ${sizeClasses[size]}`}>{days}</div>
            <div className="text-xs opacity-90">días</div>
          </div>
        )}
        
        <div className="bg-red-600 text-white rounded px-2 py-1 text-center min-w-[40px]">
          <div className={`font-bold ${sizeClasses[size]}`}>
            {String(hours).padStart(2, '0')}
          </div>
          <div className="text-xs opacity-90">hrs</div>
        </div>
        
        <div className="bg-red-600 text-white rounded px-2 py-1 text-center min-w-[40px]">
          <div className={`font-bold ${sizeClasses[size]}`}>
            {String(minutes).padStart(2, '0')}
          </div>
          <div className="text-xs opacity-90">min</div>
        </div>
        
        <div className="bg-red-600 text-white rounded px-2 py-1 text-center min-w-[40px]">
          <div className={`font-bold ${sizeClasses[size]}`}>
            {String(seconds).padStart(2, '0')}
          </div>
          <div className="text-xs opacity-90">seg</div>
        </div>
      </div>
    </div>
  );
};

export default CountdownTimer;
